import React from "react";
import * as path from "path";
import { render, Instance } from "ink";
import * as cron from "node-cron";
import pLimit from "p-limit";
import { spawn, spawnSync } from "child_process";
import { existsSync } from "fs";
import App from "../components/App";
import { DEFAULT_CONFIG } from "../constants";
import { WorktreeSyncService } from "./worktree-sync.service";
import { ConfigLoaderService } from "./config-loader.service";
import { BranchCreatedActionsService } from "./branch-created-actions.service";
import { HookExecutionService } from "./hook-execution.service";
import { PathResolutionService } from "./path-resolution.service";
import { Logger, LogOutputFn, LogLevel } from "./logger.service";
import { formatCloneSkipReason } from "../utils/clone-skip-format";
import { getErrorMessage } from "../utils/lfs-error";
import { calculateSyncDiskSpace } from "../utils/disk-space";
import { getDefaultBareRepoDir } from "../utils/git-url";
import { AppEventEmitter } from "../utils/app-events";
import { resolveMode } from "../utils/repo-mode";
import { shellEscape } from "../utils/shell-escape";
import * as fs from "fs/promises";
import { calculateDirectorySize, formatBytes } from "../utils/disk-space";
import { formatDuration } from "../utils/timing";
import { GIT_CONSTANTS, METADATA_CONSTANTS, TERMINAL_CONSTANTS } from "../constants";
import type {
  RepositoryConfig,
  HookContext,
  WorktreeStatusEntry,
  DivergedDirectoryInfo,
  RepositoryListEntry,
  RepositoryDiskUsage,
  ForceCleanRepositoryPreview,
  ForceCleanRepositoryResult,
} from "../types";

const WAIT_SYNC_FAST_TIMEOUT_MS = 2000;
const WAIT_SYNC_DEFAULT_TIMEOUT_MS = 30000;

export class InteractiveUIService {
  private app: Instance | null = null;
  private syncServices: WorktreeSyncService[];
  private configPath?: string;
  private cronSchedule?: string;
  private cronJobs: cron.ScheduledTask[] = [];
  private repositoryCount: number;
  private logBuffer: Array<{ message: string; level: "info" | "warn" | "error" }> = [];
  private uiReady = false;
  private hookExecutionService = new HookExecutionService();
  private branchCreatedActions = new BranchCreatedActionsService();
  private pathResolution = new PathResolutionService();
  private limit: ReturnType<typeof pLimit>;
  private maxProgressLines: number;
  private reloadInProgress = false;
  private isDestroyed = false;
  private events: AppEventEmitter;
  private ownsEvents: boolean;
  private unsubscribeCallbacks: Array<() => void> = [];
  private progressUnsubscribers: Array<() => void> = [];

  constructor(
    syncServices: WorktreeSyncService[],
    configPath?: string,
    cronSchedule?: string,
    maxParallel?: number,
    events?: AppEventEmitter,
  ) {
    this.ownsEvents = events === undefined;
    this.events = events ?? new AppEventEmitter();
    if (syncServices.length === 0) {
      throw new Error("InteractiveUIService requires at least one WorktreeSyncService");
    }

    this.syncServices = syncServices;
    this.configPath = configPath;
    this.cronSchedule = cronSchedule;
    this.repositoryCount = syncServices.length;
    this.maxProgressLines = Math.max(1, maxParallel ?? DEFAULT_CONFIG.PARALLELISM.MAX_REPOSITORIES);
    this.limit = pLimit(this.maxProgressLines);

    this.startBufferFlushCheck();
    this.renderUI();
    this.subscribeToServiceProgress();
    this.injectLoggersIntoServices();

    // Add initial log after a short delay to verify the pipeline works
    setTimeout(() => {
      this.addLog("🚀 sync-worktrees UI initialized", "info");
    }, 100);
  }

  public getEvents(): AppEventEmitter {
    return this.events;
  }

  private startBufferFlushCheck(): void {
    const unsubscribe = this.events.on("uiReady", () => {
      this.uiReady = true;
      this.flushLogBuffer();
      unsubscribe();
      const index = this.unsubscribeCallbacks.indexOf(unsubscribe);
      if (index !== -1) this.unsubscribeCallbacks.splice(index, 1);
    });
    this.unsubscribeCallbacks.push(unsubscribe);
  }

  private createOutputFn(): LogOutputFn {
    return (message: string, level: LogLevel) => {
      const uiLevel = level === "debug" ? "info" : level;
      this.addLog(message, uiLevel);
    };
  }

  private injectLoggersIntoServices(): void {
    const outputFn = this.createOutputFn();
    for (const service of this.syncServices) {
      const config = service.config as RepositoryConfig;
      service.updateLogger(
        new Logger({
          repoName: config.name,
          debug: config.debug,
          outputFn,
        }),
      );
    }
  }

  private subscribeToServiceProgress(): void {
    for (const unsubscribe of this.progressUnsubscribers) {
      unsubscribe();
    }
    this.progressUnsubscribers = this.syncServices.map((service, index) => {
      const repoName = this.getRepoName(index);
      if (!service.onProgress) return () => undefined;
      return service.onProgress((event) => {
        if (this.isDestroyed) return;
        this.events.emit("setSyncProgress", {
          repo: repoName,
          phase: event.phase,
          message: event.message,
          progress: event.progress,
          processed: event.processed,
          total: event.total,
        });
      });
    });
  }

  public addLog(message: string, level: "info" | "warn" | "error" = "info"): void {
    if (this.isDestroyed) return;
    if (this.uiReady) {
      this.events.emit("addLog", { message, level });
    } else {
      this.logBuffer.push({ message, level });
    }
  }

  private flushLogBuffer(): void {
    for (const log of this.logBuffer) {
      this.events.emit("addLog", { message: log.message, level: log.level });
    }
    this.logBuffer = [];
  }

  public setupCronJobs(): void {
    const scheduleGroups = new Map<string, WorktreeSyncService[]>();

    for (const service of this.syncServices) {
      if (service.config.runOnce) continue;
      const schedule = service.config.cronSchedule || this.cronSchedule;
      if (!schedule) continue;

      if (!scheduleGroups.has(schedule)) {
        scheduleGroups.set(schedule, []);
      }
      scheduleGroups.get(schedule)!.push(service);
    }

    for (const [schedule, services] of scheduleGroups) {
      const task = cron.schedule(schedule, async () => {
        await this.runSyncCycle(services, { logErrors: false });
      });
      this.cronJobs.push(task);
    }
  }

  private cancelCronJobs(): void {
    for (const job of this.cronJobs) {
      job.stop();
    }
    this.cronJobs = [];
  }

  public registerCronJob(job: cron.ScheduledTask): void {
    this.cronJobs.push(job);
  }

  private renderUI(): void {
    if (this.app) {
      this.app.unmount();
    }

    this.app = render(
      <App
        events={this.events}
        repositoryCount={this.repositoryCount}
        cronSchedule={this.cronSchedule}
        maxProgressLines={this.maxProgressLines}
        onManualSync={() => this.handleManualSync()}
        onReload={() => this.handleReload()}
        onQuit={() => this.handleQuit()}
        getRepositoryList={() => this.getRepositoryList()}
        getBranchesForRepo={(index: number) => this.getBranchesForRepo(index)}
        getDefaultBranchForRepo={(index: number) => this.getDefaultBranchForRepo(index)}
        fetchForRepo={(index: number) => this.fetchForRepo(index)}
        createAndPushBranch={(repoIndex: number, baseBranch: string, branchName: string) =>
          this.createAndPushBranch(repoIndex, baseBranch, branchName)
        }
        getWorktreesForRepo={(index: number) => this.getWorktreesForRepo(index)}
        getWorktreeStatusForRepo={(index: number) => this.getWorktreeStatusForRepo(index)}
        getRepositoryDiskUsage={(index: number) => this.getRepositoryDiskUsage(index)}
        getDivergedDirectoriesForRepo={(index: number) => this.getDivergedDirectoriesForRepo(index)}
        deleteDivergedDirectory={(repoIndex: number, name: string) => this.deleteDivergedDirectory(repoIndex, name)}
        getForceCleanPreview={() => this.getForceCleanPreview()}
        forceClean={(repoIndexes?: number[]) => this.forceClean(repoIndexes)}
        openEditorInWorktree={(path: string) => this.openEditorInWorktree(path)}
        openTerminalInWorktree={(repoIndex: number, path: string, branchName: string) =>
          this.openTerminalInWorktree(repoIndex, path, branchName)
        }
        copyBranchFiles={(repoIndex: number, baseBranch: string, targetBranch: string) =>
          this.copyBranchFiles(repoIndex, baseBranch, targetBranch)
        }
        createWorktreeForBranch={(repoIndex: number, branchName: string) =>
          this.createWorktreeForBranch(repoIndex, branchName)
        }
        executeOnBranchCreatedHooks={(repoIndex: number, context: HookContext) =>
          this.executeOnBranchCreatedHooks(repoIndex, context)
        }
      />,
      {
        alternateScreen: true,
        incrementalRendering: true,
      },
    );
  }

  private async handleManualSync(): Promise<void> {
    await this.triggerInitialSync();
  }

  public async triggerInitialSync(): Promise<void> {
    await this.runSyncCycle(this.syncServices, { logErrors: true });
  }

  private async handleReload(): Promise<void> {
    if (this.reloadInProgress) {
      return;
    }
    this.reloadInProgress = true;
    let cronJobsCancelled = false;
    try {
      if (!this.configPath) {
        this.setStatus("idle");
        return;
      }

      await this.waitForInProgressSyncs();

      this.addLog("Reloading configuration...");
      this.setStatus("syncing");

      // Validate and load new config BEFORE canceling old cron jobs
      // to prevent a window with no cron running on validation failure
      const configLoader = new ConfigLoaderService();
      const { repositories } = await configLoader.buildRepositories(this.configPath);

      const initResults = await Promise.allSettled(
        repositories.map((repoConfig) =>
          this.limit(async () => {
            const service = new WorktreeSyncService(repoConfig);
            await service.initialize();
            return {
              service,
              clonePhaseSkips: service.getRecordedSkips().map((reason) => ({
                repo: repoConfig.name || repoConfig.repoUrl,
                reason: formatCloneSkipReason(reason),
              })),
            };
          }),
        ),
      );

      const newServices: WorktreeSyncService[] = [];
      const initClonePhaseSkips: Array<{ repo: string; reason: string }> = [];
      for (const result of initResults) {
        if (result.status === "fulfilled") {
          newServices.push(result.value.service);
          initClonePhaseSkips.push(...result.value.clonePhaseSkips);
        } else {
          this.addLog(`Failed to initialize repository: ${result.reason}`, "error");
        }
      }

      if (newServices.length === 0) {
        throw new Error("No repositories could be initialized from the configuration");
      }

      // Cancel old cron jobs only after new config is validated and services initialized
      this.cancelCronJobs();
      cronJobsCancelled = true;

      this.syncServices = newServices;
      this.repositoryCount = this.syncServices.length;
      this.subscribeToServiceProgress();
      this.injectLoggersIntoServices();

      const uniqueSchedules = [...new Set(this.syncServices.map((s) => s.config.cronSchedule))];
      this.cronSchedule = uniqueSchedules.length === 1 ? uniqueSchedules[0] : undefined;

      this.setupCronJobs();

      this.events.emit("updateRepositoryCount", this.repositoryCount);
      this.events.emit("updateCronSchedule", this.cronSchedule);

      const {
        failures,
        skipped,
        clonePhaseSkips: syncClonePhaseSkips,
        attempted,
      } = await this.runSyncServices(this.syncServices);
      const clonePhaseSkips = [...initClonePhaseSkips, ...syncClonePhaseSkips];
      await this.recordSyncOutcome({ failures, skipped, attempted });
      this.setStatus("idle");

      for (const skip of skipped) {
        this.addLog(`Sync skipped for '${skip.repo}': ${skip.reason}`, "warn");
      }
      for (const skip of clonePhaseSkips) {
        this.addLog(`Clone-mode skip for '${skip.repo}': ${skip.reason}`, "warn");
      }
      if (clonePhaseSkips.length > 0) {
        this.addLog(`⚠️  ${clonePhaseSkips.length} clone-mode skip(s) during reload`, "warn");
      }
      if (failures.length > 0) {
        for (const failure of failures) {
          this.addLog(`Failed to sync repository '${failure.repo}': ${failure.error}`, "error");
        }
        this.addLog(`Reload completed with ${failures.length} repository failure(s)`, "warn");
      }
    } catch (error) {
      this.addLog(`Reload failed: ${(error as Error).message}`, "error");
      if (cronJobsCancelled) {
        this.setupCronJobs();
      }
      this.setStatus("idle");
    } finally {
      this.reloadInProgress = false;
    }
  }

  private async handleQuit(): Promise<void> {
    await this.destroy();
    process.exit(0);
  }

  private async waitForInProgressSyncs(timeoutMs: number = WAIT_SYNC_DEFAULT_TIMEOUT_MS): Promise<void> {
    const inProgressServices = this.syncServices.filter((s) => s.isSyncInProgress());

    if (inProgressServices.length === 0) {
      return;
    }

    this.addLog(`Waiting for ${inProgressServices.length} in-progress sync(s) to finish...`, "info");

    const syncChecks = inProgressServices.map(async (service) => {
      const checkInterval = 500;
      const startTime = Date.now();

      while (service.isSyncInProgress()) {
        if (Date.now() - startTime > timeoutMs) {
          throw new Error("Timeout waiting for sync operations to complete");
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    });

    try {
      await Promise.all(syncChecks);
    } catch {
      this.addLog(
        `Warning: Timeout waiting for sync operations to complete after ${formatDuration(timeoutMs)}. Proceeding with potential data loss risk.`,
        "warn",
      );
    }
  }

  public updateLastSyncTime(): void {
    if (this.isDestroyed) return;
    this.events.emit("updateLastSyncTime");
  }

  public setStatus(status: "idle" | "syncing"): void {
    if (this.isDestroyed) return;
    this.events.emit("setStatus", status);
    if (status === "idle") {
      this.events.emit("setSyncProgress", null);
    }
  }

  public setDiskSpace(diskSpace: string): void {
    if (this.isDestroyed) return;
    this.events.emit("setDiskSpace", diskSpace);
  }

  public async calculateAndUpdateDiskSpace(): Promise<void> {
    try {
      const bareRepoDirs = this.syncServices.map(
        (service) => service.config.bareRepoDir || getDefaultBareRepoDir(service.config.repoUrl),
      );
      const worktreeDirs = this.syncServices.map((service) => service.config.worktreeDir);

      const diskSpace = await calculateSyncDiskSpace(bareRepoDirs, worktreeDirs);
      this.setDiskSpace(diskSpace);
    } catch (error) {
      this.addLog(`Failed to calculate disk space: ${error instanceof Error ? error.message : String(error)}`, "error");
      this.setDiskSpace("N/A");
    }
  }

  public getRepositoryList(): RepositoryListEntry[] {
    return this.syncServices.map((service, index) => ({
      index,
      name: this.getRepoName(index),
      repoUrl: service.config.repoUrl,
    }));
  }

  private getRepoName(index: number): string {
    const service = this.syncServices[index];
    return (service.config as RepositoryConfig).name || `repo-${index}`;
  }

  public async getRepositoryDiskUsage(repoIndex: number): Promise<RepositoryDiskUsage> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const config = service.config;
    const repoName = this.getRepoName(repoIndex);
    const mode = resolveMode(config);
    const sizeTargets: Array<{ kind: "bare" | "worktree"; path: string }> = [
      ...(mode === "worktree"
        ? [{ kind: "bare" as const, path: config.bareRepoDir || getDefaultBareRepoDir(config.repoUrl) }]
        : []),
      { kind: "worktree", path: config.worktreeDir },
    ];

    let bareSizeBytes = 0;
    let worktreeSizeBytes = 0;
    const errors: string[] = [];

    for (const target of sizeTargets) {
      try {
        const size = await calculateDirectorySize(target.path);
        if (target.kind === "bare") {
          bareSizeBytes = size;
        } else {
          worktreeSizeBytes = size;
        }
      } catch (error) {
        errors.push(`${target.path}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    const sizeBytes = bareSizeBytes + worktreeSizeBytes;
    const failedAllPaths = errors.length === sizeTargets.length;
    const partialFailure = errors.length > 0 && !failedAllPaths;

    return {
      repoIndex,
      repoName,
      sizeBytes: failedAllPaths ? null : sizeBytes,
      sizeFormatted: failedAllPaths ? "N/A" : partialFailure ? `≥${formatBytes(sizeBytes)}` : formatBytes(sizeBytes),
      bareSizeBytes,
      worktreeSizeBytes,
      error: errors.length > 0 ? errors.join("; ") : undefined,
    };
  }

  public async getBranchesForRepo(repoIndex: number): Promise<string[]> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    if (!service.isInitialized() && !service.isCloneMode()) {
      return [];
    }
    // Clone-mode branch listing hits the network (ls-remote) whether or not
    // the clone is initialized — fail to an empty list in both cases so the
    // wizard's fetch-and-retry path handles it uniformly.
    try {
      return await service.getRemoteBranches();
    } catch {
      return [];
    }
  }

  public getDefaultBranchForRepo(repoIndex: number): string {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const gitService = service.getGitService();
    return gitService.getDefaultBranch();
  }

  public async fetchForRepo(repoIndex: number): Promise<void> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const result = await service.runQueuedRepoOperation(async () => {
      // Use the unlocked init path: initialize() re-enters the repo mutex and would
      // self-deadlock inside this queued operation.
      if (!service.isInitialized()) {
        await service.initializeUnlocked();
      }
      if (service.isCloneMode()) {
        // Clone-mode tracks a single branch; there is nothing to fetch-all here.
        // Branch discovery is a live `git ls-remote` performed when the picker opens.
        return;
      }
      await service.getGitService().fetchAll();
    });
    if (!result.started) {
      throw new Error("Another process holds the repository lock; fetch skipped. Try again.");
    }
  }

  public async createAndPushBranch(
    repoIndex: number,
    baseBranch: string,
    branchName: string,
  ): Promise<{ success: boolean; finalName: string; error?: string }> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      return { success: false, finalName: branchName, error: `Invalid repository index: ${repoIndex}` };
    }

    const service = this.syncServices[repoIndex];
    const gitService = service.getGitService();

    // Serialize branch+push behind any in-flight sync so it can't race git's
    // index/refs. addWorktree (createWorktreeForBranch) is a separate queued op.
    const result = await service.runQueuedRepoOperation(async () => {
      const maxAttempts = 10;
      let finalName = branchName;
      let suffix = 0;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          await gitService.createBranch(finalName, baseBranch);
          await gitService.pushBranch(finalName);
          return { success: true, finalName };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (errorMessage.includes("already exists")) {
            suffix++;
            finalName = `${branchName}-${suffix}`;
            continue;
          }
          return { success: false, finalName: branchName, error: errorMessage };
        }
      }

      return { success: false, finalName: branchName, error: `Failed to create branch after ${maxAttempts} attempts` };
    });

    if (!result.started) {
      return {
        success: false,
        finalName: branchName,
        error: "Another process holds the repository lock; branch not created. Try again.",
      };
    }
    return result.value;
  }

  public async getWorktreesForRepo(repoIndex: number): Promise<Array<{ path: string; branch: string }>> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    return this.getWorktreesFromService(service);
  }

  public async getWorktreeStatusForRepo(repoIndex: number): Promise<WorktreeStatusEntry[]> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const gitService = service.getGitService();
    const worktrees = await this.getWorktreesFromService(service);

    const results = await Promise.allSettled(
      worktrees.map(async (wt) => {
        const status = await gitService.getFullWorktreeStatus(wt.path, true);
        return { branch: wt.branch, path: wt.path, status };
      }),
    );

    return results
      .filter((r): r is PromiseFulfilledResult<WorktreeStatusEntry> => r.status === "fulfilled")
      .map((r) => r.value);
  }

  private async getWorktreesFromService(
    service: WorktreeSyncService,
  ): Promise<Array<{ path: string; branch: string }>> {
    const worktreeProvider = service as WorktreeSyncService & {
      getWorktrees?: () => Promise<Array<{ path: string; branch: string }>>;
    };
    if (typeof worktreeProvider.getWorktrees === "function") {
      return worktreeProvider.getWorktrees();
    }
    return service.getGitService().getWorktrees();
  }

  public async getDivergedDirectoriesForRepo(repoIndex: number): Promise<DivergedDirectoryInfo[]> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      return [];
    }

    const service = this.syncServices[repoIndex];
    const worktreeDir = service.config.worktreeDir;
    const divergedDir = path.join(worktreeDir, GIT_CONSTANTS.DIVERGED_DIR_NAME);

    let dirEntries: import("fs").Dirent[];
    try {
      dirEntries = await fs.readdir(divergedDir, { withFileTypes: true, encoding: "utf-8" });
    } catch {
      return [];
    }

    const subdirs = dirEntries.filter((e) => e.isDirectory());

    const results = await Promise.allSettled(
      subdirs.map(async (entry): Promise<DivergedDirectoryInfo> => {
        const fullPath = path.join(divergedDir, entry.name);
        const infoFilePath = path.join(fullPath, METADATA_CONSTANTS.DIVERGED_INFO_FILE);

        let originalBranch = entry.name;
        let divergedAt = "";
        let keepRef: string | undefined;

        try {
          const infoContent = await fs.readFile(infoFilePath, "utf-8");
          const info = JSON.parse(infoContent);
          if (typeof info.originalBranch === "string") originalBranch = info.originalBranch;
          if (typeof info.divergedAt === "string") divergedAt = info.divergedAt;
          if (typeof info.keepRef === "string") keepRef = info.keepRef;
        } catch {
          // Extract date and branch from directory name pattern: YYYY-MM-DD-branch-suffix
          const match = entry.name.match(/^(\d{4}-\d{2}-\d{2})-(.+?)(?:-[a-f0-9]+)?$/);
          if (match) {
            divergedAt = match[1];
            originalBranch = match[2];
          }
        }

        const sizeBytes = await calculateDirectorySize(fullPath).catch(() => 0);
        const sizeFormatted = formatBytes(sizeBytes);

        return {
          name: entry.name,
          path: fullPath,
          originalBranch,
          divergedAt,
          sizeBytes,
          sizeFormatted,
          keepRef,
        };
      }),
    );

    return results
      .filter((r): r is PromiseFulfilledResult<DivergedDirectoryInfo> => r.status === "fulfilled")
      .map((r) => r.value)
      .sort((a, b) => b.divergedAt.localeCompare(a.divergedAt));
  }

  public async deleteDivergedDirectory(repoIndex: number, name: string): Promise<void> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const worktreeDir = service.config.worktreeDir;
    const divergedBase = path.resolve(worktreeDir, GIT_CONSTANTS.DIVERGED_DIR_NAME);

    if (!name || name === "." || name === ".." || name.includes("/") || name.includes("\\")) {
      throw new Error(`Invalid diverged directory name: "${name}"`);
    }

    const targetPath = path.join(divergedBase, name);

    if (!this.pathResolution.isPathInsideBaseDir(targetPath, divergedBase)) {
      throw new Error(`Path traversal rejected: "${name}" resolves outside the diverged directory`);
    }

    let keepRef: string | undefined;
    try {
      const info = JSON.parse(await fs.readFile(path.join(targetPath, METADATA_CONSTANTS.DIVERGED_INFO_FILE), "utf-8"));
      if (typeof info.keepRef === "string") keepRef = info.keepRef;
    } catch {
      // Legacy entries have no keep ref metadata.
    }
    await service.discardDivergedDirectory(targetPath, keepRef);
    this.addLog(`🗑️ Deleted diverged directory: ${name}`, "info");
  }

  public async getForceCleanPreview(): Promise<ForceCleanRepositoryPreview[]> {
    return Promise.all(
      this.syncServices.map((service, repoIndex) =>
        this.limit(async () => {
          const repoName = this.getRepoName(repoIndex);
          try {
            return { repoIndex, repoName, preview: await service.getForceCleanPreview() };
          } catch (error) {
            return { repoIndex, repoName, error: getErrorMessage(error) };
          }
        }),
      ),
    );
  }

  // `repoIndexes` is what the confirmation actually showed. A repo whose preview
  // failed is not in that list and is skipped: purging it would destroy content
  // the user was never shown a count for.
  public async forceClean(repoIndexes?: number[]): Promise<ForceCleanRepositoryResult[]> {
    const selected = repoIndexes ? new Set(repoIndexes) : null;
    const results = await Promise.all(
      this.syncServices.map((service, repoIndex) =>
        this.limit(async () => {
          const repoName = this.getRepoName(repoIndex);
          if (selected && !selected.has(repoIndex)) {
            return { repoIndex, repoName, error: "skipped: cleanup preview was unavailable" };
          }
          try {
            const result = await service.forceClean();
            const level = result.errors.length > 0 ? "warn" : "info";
            this.addLog(
              `🧹 Force clean ${repoName}: deleted ${result.trashDeleted} trash entries and ${result.keepRefsDeleted} recovery refs; GC ${result.gcSucceeded ? "complete" : "failed"}`,
              level,
            );
            return { repoIndex, repoName, result };
          } catch (error) {
            const message = getErrorMessage(error);
            this.addLog(`Force clean ${repoName} failed: ${message}`, "error");
            return { repoIndex, repoName, error: message };
          }
        }),
      ),
    );
    await this.calculateAndUpdateDiskSpace();
    return results;
  }

  public async createWorktreeForBranch(repoIndex: number, branchName: string): Promise<void> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      throw new Error(`Invalid repository index: ${repoIndex}`);
    }

    const service = this.syncServices[repoIndex];
    const gitService = service.getGitService();
    const worktreeDir = service.config.worktreeDir;
    const worktreePath = this.pathResolution.getBranchWorktreePath(worktreeDir, branchName);

    const result = await service.runQueuedRepoOperation(async () => {
      if (service.isCloneMode()) {
        // The wizard just created and pushed this branch — switching to it is
        // intentional config drift; checkoutBranch warns to update config.branch.
        await service.checkoutBranch(branchName, { allowConfigDrift: true });
        return;
      }
      await gitService.addWorktree(branchName, worktreePath);
    });
    if (!result.started) {
      throw new Error("Another process holds the repository lock; worktree not created. Try again.");
    }
  }

  public openEditorInWorktree(worktreePath: string): { success: boolean; error?: string } {
    const editor = process.env.EDITOR || process.env.VISUAL || "code";
    // EDITOR may include flags (e.g. "code -w"); spawn without a shell treats
    // the whole string as the binary name, so split command and args ourselves.
    const [command, ...editorArgs] = editor.trim().split(/\s+/);

    try {
      const child = spawn(command, [...editorArgs, worktreePath], {
        detached: true,
        stdio: "ignore",
      });

      child.on("error", (err) => {
        this.addLog(`Failed to open editor '${editor}': ${err.message}`, "error");
        this.addLog("Set EDITOR or VISUAL environment variable to your preferred editor", "warn");
      });

      child.unref();

      // Return success optimistically - spawn errors are async and will be logged
      // to the UI when they occur. For detached processes, we can't reliably
      // catch spawn errors synchronously.
      return { success: true };
    } catch (err) {
      // This catches synchronous errors like ENOENT when the command doesn't exist
      const errorMessage = err instanceof Error ? err.message : String(err);
      this.addLog(`Failed to open editor '${editor}': ${errorMessage}`, "error");
      return { success: false, error: errorMessage };
    }
  }

  public openTerminalInWorktree(
    repoIndex: number,
    worktreePath: string,
    branchName: string,
  ): { success: boolean; error?: string } {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      const message = `Invalid repository index: ${repoIndex}`;
      this.addLog(message, "error");
      return { success: false, error: message };
    }
    const repoName = this.getRepoName(repoIndex);
    const sanitizedBranch = this.pathResolution.sanitizeBranchName(branchName);
    const sessionName = `${repoName}-${sanitizedBranch}`;
    const tmuxCommand = `tmux new-session -A -s ${shellEscape(sessionName)} -c ${shellEscape(worktreePath)}`;

    const launcher = this.resolveTerminalLauncher(tmuxCommand);
    if (!launcher) {
      const message =
        "No terminal launcher found. Set SYNC_WORKTREES_TERMINAL or $TERMINAL to a terminal emulator command.";
      this.addLog(message, "error");
      return { success: false, error: message };
    }

    try {
      const child = spawn(launcher.command, launcher.args, {
        detached: true,
        stdio: "ignore",
      });

      child.on("error", (err) => {
        this.addLog(`Failed to open terminal '${launcher.command}': ${err.message}`, "error");
        this.addLog("Set SYNC_WORKTREES_TERMINAL to your preferred terminal command", "warn");
      });

      child.unref();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      this.addLog(`Failed to open terminal '${launcher.command}': ${errorMessage}`, "error");
      return { success: false, error: errorMessage };
    }
  }

  private resolveTerminalLauncher(tmuxCommand: string): { command: string; args: string[] } | null {
    const override = this.parseCommandString(process.env[TERMINAL_CONSTANTS.ENV_OVERRIDE]);
    if (override) {
      // Wrap the tmux command in `sh -c` so terminal emulators that exec their trailing
      // arg as a program name (e.g. `alacritty -e`, `kitty -e`) can run the composite command.
      return { command: override.command, args: [...override.args, "sh", "-c", tmuxCommand] };
    }

    switch (process.platform) {
      case "darwin": {
        // Ghostty cannot be launched directly from the CLI on macOS; use `open -na` instead.
        const ghosttyPaths = ["/Applications/Ghostty.app", `${process.env.HOME}/Applications/Ghostty.app`];
        if (ghosttyPaths.some((p) => existsSync(p))) {
          return {
            command: "open",
            args: ["-na", "Ghostty.app", "--args", "-e", "sh", "-c", tmuxCommand],
          };
        }
        const escapedTmuxCommand = tmuxCommand.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        const script = `tell application "Terminal" to do script "${escapedTmuxCommand}"`;
        return { command: "osascript", args: ["-e", script] };
      }
      case "linux": {
        const envTerminal = this.parseCommandString(process.env[TERMINAL_CONSTANTS.ENV_FALLBACK]);
        if (envTerminal) {
          return { command: envTerminal.command, args: [...envTerminal.args, "-e", "sh", "-c", tmuxCommand] };
        }
        for (const candidate of TERMINAL_CONSTANTS.LINUX_CANDIDATES) {
          if (this.commandExists(candidate)) {
            if (candidate === "gnome-terminal") {
              return { command: candidate, args: ["--", "sh", "-c", tmuxCommand] };
            }
            return { command: candidate, args: ["-e", "sh", "-c", tmuxCommand] };
          }
        }
        return null;
      }
      default:
        return null;
    }
  }

  private parseCommandString(raw: string | undefined): { command: string; args: string[] } | null {
    if (!raw || raw.trim().length === 0) return null;
    const parts = raw.trim().split(/\s+/);
    return { command: parts[0], args: parts.slice(1) };
  }

  private commandExists(command: string): boolean {
    try {
      const result = spawnSync("which", [command], { stdio: "ignore" });
      return result.status === 0;
    } catch {
      return false;
    }
  }

  private async runSyncCycle(
    services: WorktreeSyncService[],
    options: { logErrors: boolean },
  ): Promise<Array<{ repo: string; error: string }>> {
    this.setStatus("syncing");

    try {
      const { failures, skipped, partialSkips, clonePhaseSkips, attempted } = await this.runSyncServices(services);

      if (options.logErrors) {
        for (const failure of failures) {
          this.addLog(`Failed to sync repository '${failure.repo}': ${failure.error}`, "error");
        }
      }
      for (const skip of skipped) {
        this.addLog(`Sync skipped for '${skip.repo}': ${skip.reason}`, "warn");
      }
      for (const skip of clonePhaseSkips) {
        this.addLog(`Clone-mode skip for '${skip.repo}': ${skip.reason}`, "warn");
      }
      if (clonePhaseSkips.length > 0) {
        this.addLog(`⚠️  ${clonePhaseSkips.length} clone-mode skip(s) this cycle`, "warn");
      }
      for (const partial of partialSkips) {
        this.addLog(`${partial.repo}: ${partial.reason}`, "info");
      }

      await this.recordSyncOutcome({ failures, skipped, attempted });
      return failures;
    } finally {
      this.setStatus("idle");
    }
  }

  private async recordSyncOutcome(outcome: {
    failures: Array<{ repo: string; error: string }>;
    skipped: Array<{ repo: string; reason: string }>;
    attempted: number;
  }): Promise<void> {
    const allSkipped =
      outcome.attempted > 0 && outcome.skipped.length === outcome.attempted && outcome.failures.length === 0;
    if (allSkipped) return;
    this.updateLastSyncTime();
    await this.calculateAndUpdateDiskSpace();
  }

  private async runSyncServices(services: WorktreeSyncService[]): Promise<{
    failures: Array<{ repo: string; error: string }>;
    skipped: Array<{ repo: string; reason: string }>;
    partialSkips: Array<{ repo: string; reason: string }>;
    clonePhaseSkips: Array<{ repo: string; reason: string }>;
    attempted: number;
  }> {
    const syncResults = await Promise.allSettled(
      services.map((service) => {
        const repoName = (service.config as RepositoryConfig).name || service.config.repoUrl;
        return this.limit(async () => {
          service.clearRecordedSkips();
          try {
            if (!service.isInitialized()) {
              await service.initialize();
            }
            const result = await service.sync();
            return { service, result };
          } finally {
            this.events.emit("setSyncProgress", {
              repo: repoName,
              phase: "complete",
              message: "Finished",
              completed: true,
            });
          }
        }).catch((error) => {
          throw Object.assign(error instanceof Error ? error : new Error(String(error)), { repoName });
        });
      }),
    );

    const failures: Array<{ repo: string; error: string }> = [];
    const skipped: Array<{ repo: string; reason: string }> = [];
    const partialSkips: Array<{ repo: string; reason: string }> = [];
    const clonePhaseSkips: Array<{ repo: string; reason: string }> = [];
    for (let i = 0; i < syncResults.length; i++) {
      const result = syncResults[i];
      const repoName = (services[i].config as RepositoryConfig).name || services[i].config.repoUrl;
      if (result.status === "rejected") {
        const fallbackName = (result.reason as { repoName?: string })?.repoName ?? repoName;
        const errorMessage = result.reason instanceof Error ? result.reason.message : String(result.reason);
        failures.push({ repo: fallbackName, error: errorMessage });
      } else if (result.value.result && result.value.result.started === false) {
        skipped.push({ repo: repoName, reason: `sync skipped: ${result.value.result.reason}` });
      } else if (result.status === "fulfilled" && result.value.result?.started === true) {
        const outcome = result.value.result.outcome;
        if (outcome?.counts.failed) {
          failures.push({ repo: repoName, error: `${outcome.counts.failed} sync action(s) failed` });
        }
        // Per-action skips are informational; the repo did complete its sync
        // attempt. Surface as a separate channel so updateLastSyncTime still
        // runs and the per-cycle log stays at info-level.
        if (outcome?.mode === "worktree" && outcome.counts.skipped > 0) {
          partialSkips.push({ repo: repoName, reason: `${outcome.counts.skipped} sync action(s) skipped` });
        }
      }
      for (const reason of services[i].getRecordedSkips()) {
        clonePhaseSkips.push({ repo: repoName, reason: formatCloneSkipReason(reason) });
      }
    }

    return { failures, skipped, partialSkips, clonePhaseSkips, attempted: services.length };
  }

  private buildUiLogger(): Logger {
    return new Logger({
      outputFn: (msg: string, level: LogLevel): void => {
        const uiLevel: "info" | "warn" | "error" = level === "warn" ? "warn" : level === "error" ? "error" : "info";
        this.addLog(msg, uiLevel);
      },
    });
  }

  public executeOnBranchCreatedHooks(repoIndex: number, context: HookContext): void {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      return;
    }

    const service = this.syncServices[repoIndex];
    const config = service.config;
    const repoName = (config as RepositoryConfig).name || config.repoUrl;

    this.branchCreatedActions.runHooks({
      config,
      repoName,
      branchName: context.branchName,
      worktreePath: context.worktreePath,
      baseBranch: context.baseBranch,
      logger: this.buildUiLogger(),
      hookExecutionService: this.hookExecutionService,
    });
  }

  public async copyBranchFiles(repoIndex: number, baseBranch: string, targetBranch: string): Promise<void> {
    if (repoIndex < 0 || repoIndex >= this.syncServices.length) {
      return;
    }

    const service = this.syncServices[repoIndex];
    const config = service.config;

    if (!config.filesToCopyOnBranchCreate?.length) {
      return;
    }

    const worktrees = await this.getWorktreesFromService(service);

    const sourceWorktree = worktrees.find((w) => w.branch === baseBranch);
    const targetWorktree = worktrees.find((w) => w.branch === targetBranch);

    if (!sourceWorktree || !targetWorktree) {
      this.addLog(`Could not find worktrees for file copy: source=${baseBranch}, target=${targetBranch}`, "warn");
      return;
    }

    await this.branchCreatedActions.copyFiles({
      config,
      branchName: targetBranch,
      worktreePath: targetWorktree.path,
      sourceDir: sourceWorktree.path,
      logger: this.buildUiLogger(),
    });
  }

  public async destroy(fast = false): Promise<void> {
    this.isDestroyed = true;
    this.cancelCronJobs();

    try {
      await this.waitForInProgressSyncs(fast ? WAIT_SYNC_FAST_TIMEOUT_MS : WAIT_SYNC_DEFAULT_TIMEOUT_MS);
    } catch {
      // Best effort - proceed with teardown even if syncs don't finish
    }

    this.hookExecutionService.cleanup();
    if (this.app) {
      this.app.unmount();
      this.app = null;
    }
    for (const unsubscribe of this.unsubscribeCallbacks) {
      unsubscribe();
    }
    this.unsubscribeCallbacks = [];
    for (const unsubscribe of this.progressUnsubscribers) {
      unsubscribe();
    }
    this.progressUnsubscribers = [];
    if (this.ownsEvents) {
      this.events.removeAllListeners();
    }
    this.uiReady = false;
    this.logBuffer = [];
  }
}
